'use client';

import { useTranslations } from 'next-intl';
import { AlertTriangle, CheckCircle2, MousePointerClick } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { SortingSession } from '../types';

interface SortingSummaryPanelProps {
  session: SortingSession;
  onMoveContainer: () => void;
  selectedItemId: number | null;
  className?: string;
}

export function SortingSummaryPanel({
  session,
  onMoveContainer,
  selectedItemId,
  className
}: SortingSummaryPanelProps) {
  const t = useTranslations('sorting');

  const isInProgress = session.status === 'in_progress';
  const isCompleted = session.status === 'completed';

  const selectedItem = session.items?.find(
    (item) => item.id === selectedItemId
  );

  const totalPages = selectedItem?.registration?.expected_page_count ?? 0;
  const isContainerAccepted = selectedItem?.status === 'scanned';
  const completedPages = isContainerAccepted
    ? totalPages
    : (selectedItem?.page_scans?.length ?? 0);
  const remainingPages = isContainerAccepted
    ? 0
    : Math.max(0, totalPages - completedPages);

  const canRefuse =
    selectedItem &&
    (selectedItem.status === 'pending' || selectedItem.status === 'scanning') &&
    isInProgress;

  return (
    <Card className={cn('flex flex-col lg:h-full', className)}>
      <CardContent className='flex flex-1 flex-col gap-4 pt-6'>
        {isCompleted && (
          <div className='rounded-lg border border-green-200 bg-green-50 p-3 text-center dark:border-green-800 dark:bg-green-900/30'>
            <CheckCircle2 className='mx-auto mb-1 h-5 w-5 text-green-600' />
            <p className='text-sm font-medium text-green-700 dark:text-green-400'>
              {t('statusCompleted')}
            </p>
          </div>
        )}

        {isInProgress && selectedItem && (
          <div className='space-y-2'>
            <p className='text-muted-foreground text-xs font-medium'>
              {t('containerSummary')}
            </p>

            <div className='divide-y rounded-lg border'>
              <div className='flex items-center justify-between px-4 py-3'>
                <span className='text-muted-foreground text-sm'>
                  {t('totalPages')}
                </span>
                <span className='text-3xl font-bold'>{totalPages}</span>
              </div>

              <div className='flex items-center justify-between px-4 py-3'>
                <span className='text-muted-foreground text-sm'>
                  {t('completedCount')}
                </span>
                <span className='text-3xl font-bold text-green-600'>
                  {completedPages}
                </span>
              </div>

              <div className='flex items-center justify-between px-4 py-3'>
                <span className='text-muted-foreground text-sm'>
                  {t('remainingCount')}
                </span>
                <span className='text-3xl font-bold text-orange-500'>
                  {remainingPages}
                </span>
              </div>
            </div>
          </div>
        )}

        {isInProgress && (
          <div className='space-y-2'>
            <p className='text-muted-foreground text-xs font-medium'>
              {t('operations')}
            </p>

            {selectedItem && (
              <Button
                className='w-full'
                onClick={onMoveContainer}
                disabled={!canRefuse}
              >
                <AlertTriangle className='me-2 h-4 w-4' />
                {t('moveContainer')}
              </Button>
            )}

            <p className='text-muted-foreground rounded-lg border border-dashed p-3 text-center text-xs'>
              {t('autoCloseHint')}
            </p>
          </div>
        )}

        {isInProgress && !selectedItem && (
          <div className='rounded-lg border border-dashed p-6 text-center'>
            <MousePointerClick className='text-muted-foreground mx-auto mb-2 h-6 w-6' />
            <p className='text-muted-foreground text-sm'>
              {t('selectContainerHint')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
